import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, ChevronRight, Loader2 } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const NavigationModal = ({ 
  address, 
  lat, 
  lng, 
  onClose, 
  savedNavApp = '', 
  savedNavMode = 'address', 
  onSavePreference 
}) => {
  const [step, setStep] = useState(savedNavApp ? 'launching' : 'choose_mode');
  const [selectedMode, setSelectedMode] = useState(savedNavMode || 'address');
  const [selectedApp, setSelectedApp] = useState(savedNavApp || '');
  const [rememberChoice, setRememberChoice] = useState(false);
  const [geocodedCoords, setGeocodedCoords] = useState({ lat: lat, lng: lng });
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Geocode address if no coordinates provided
  useEffect(() => {
    const geocodeAddress = async () => {
      if (!lat && !lng && address && GOOGLE_MAPS_API_KEY) {
        setIsGeocoding(true);
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`
          );
          const data = await response.json();
          if (data.results && data.results[0]) {
            const location = data.results[0].geometry.location;
            setGeocodedCoords({ lat: location.lat, lng: location.lng });
          }
        } catch (err) {
          console.error('Geocoding error:', err);
        } finally {
          setIsGeocoding(false);
        }
      }
    };
    geocodeAddress();
  }, [address, lat, lng]);

  // Use provided coords or geocoded coords
  const finalLat = lat || geocodedCoords.lat;
  const finalLng = lng || geocodedCoords.lng;
  const hasCoordinates = finalLat && finalLng;

  // Build URLs based on mode
  const getGoogleMapsUrl = (mode) => {
    if (mode === 'coordinates' && finalLat && finalLng) {
      return `https://www.google.com/maps/dir/?api=1&destination=${finalLat},${finalLng}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };
  
  const getWazeUrl = (mode) => {
    if (mode === 'coordinates' && finalLat && finalLng) {
      return `https://waze.com/ul?ll=${finalLat},${finalLng}&navigate=yes`;
    }
    return `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
  };

  // If user has saved preference, launch immediately
  useEffect(() => {
    if (savedNavApp && step === 'launching') {
      const mode = savedNavMode || 'address';
      const url = savedNavApp === 'google_maps' ? getGoogleMapsUrl(mode) : getWazeUrl(mode);
      window.open(url, '_blank');
      onClose();
    }
  }, [savedNavApp, savedNavMode, step]);

  const handleAppSelect = (app) => {
    setSelectedApp(app);
    
    // Save preference if checkbox is checked
    if (rememberChoice && onSavePreference) {
      onSavePreference(app, selectedMode);
    }
    
    // Launch navigation
    const url = app === 'google_maps' ? getGoogleMapsUrl(selectedMode) : getWazeUrl(selectedMode);
    window.open(url, '_blank');
    onClose();
  };

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    setStep('choose_app');
  };

  // If launching with saved preference, show loading briefly
  if (step === 'launching') {
    return (
      <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full mx-auto mb-3" />
          <p className="text-gray-600">Opening navigation...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-xs overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 text-center">Navigate to</h3>
          <p className="text-xs text-gray-500 text-center mt-1 line-clamp-2">{address}</p>
        </div>

        {step === 'choose_mode' && (
          <div className="p-2">
            <button
              onClick={() => handleModeSelect('address')}
              className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
              data-testid="nav-mode-address"
            >
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <MapPin size={24} className="text-emerald-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-900">Use Address</p>
                <p className="text-xs text-gray-500">Navigate using street address</p>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </button>
            
            {/* Show coordinates option - either from stored coords or geocoded */}
            {isGeocoding ? (
              <div className="w-full flex items-center gap-3 p-3 text-gray-400">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Loader2 size={24} className="text-gray-400 animate-spin" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">Getting coordinates...</p>
                  <p className="text-xs">Looking up GPS location</p>
                </div>
              </div>
            ) : hasCoordinates ? (
              <button
                onClick={() => handleModeSelect('coordinates')}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
                data-testid="nav-mode-coords"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Navigation size={24} className="text-purple-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Use Coordinates</p>
                  <p className="text-xs text-gray-500">More precise GPS location</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            ) : (
              <div className="w-full flex items-center gap-3 p-3 text-gray-400">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Navigation size={24} className="text-gray-300" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">Coordinates unavailable</p>
                  <p className="text-xs">No GPS data for this address</p>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'choose_app' && (
          <>
            <div className="p-2">
              <button
                onClick={() => handleAppSelect('google_maps')}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
                data-testid="nav-google-maps"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <MapPin size={24} className="text-blue-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Google Maps</p>
                  <p className="text-xs text-gray-500">Open in Google Maps</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
              <button
                onClick={() => handleAppSelect('waze')}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
                data-testid="nav-waze"
              >
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                  <Navigation size={24} className="text-cyan-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Waze</p>
                  <p className="text-xs text-gray-500">Open in Waze</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            </div>
            <div className="px-4 pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={rememberChoice}
                  onChange={(e) => setRememberChoice(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">Remember my choice</span>
              </label>
            </div>
          </>
        )}

        <div className="p-2 border-t border-gray-100">
          {step === 'choose_app' && (
            <button
              onClick={() => setStep('choose_mode')}
              className="w-full p-3 text-gray-500 font-medium hover:bg-gray-50 rounded-xl transition-colors mb-1"
            >
              Back
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full p-3 text-gray-500 font-medium hover:bg-gray-50 rounded-xl transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default NavigationModal;
